<template>
	<div class="home-container relative">
		<Header class="absolute z-3" />
		<div class="globe-placeholder height-full width-full absolute top-0">
			<DestinationStateDialog
				v-if="showDestinationDialog"
				:visited="isVisited"
				:wished="isWished"
				:destination="selectedDestination"
				:is-x-s="isXS"
				:is-loading="isLoadingDestination"
				@toggle-visited="toggleVisited"
				@toggle-wishlist="toggleWishlist"
				@open-trip="openTripDialog"
				@close="closeDestination"
			/>
			<Transition name="globe-fade">
				<Globe
					v-if="!isLoadingProfile && profileCountry?.location"
					class="height-full width-full"
					ref="globe"
					:wishlist="wishlist"
					:visited="visited"
					:center="profileCountry.location"
					:is-x-s="isXS"
					@select="openDestinationDialog"
				/>
			</Transition>
		</div>
		<Content v-if="isXS" :is-mobile="isXS" class="absolute z-10 pt-8 pointer-events-none">
			<div class="d-flex justify-end gap-1 relative">
				<div class="pointer-events-all">
					<HeaderNotifications
						class="map-button rounded-2 mb-1"
						:user-id="profile?.id"
						:is-x-s="isXS"
					/>
					<div class="map-button rounded-2">
						<Dropdown>
							<template #trigger="{ toggle, isActive }">
								<PButton
									:type="isActive ? 'filled' : 'text'"
									:color="isActive ? 'reverse' : 'neutral'"
									@click="toggle"
								>
									<Icon class="text-primary-800" icon="info-circle" radius="22px" />
								</PButton>
							</template>
							<template #default>
								<ProfileDestinationsLegend class="width-fit-content" />
							</template>
						</Dropdown>
					</div>
				</div>
			</div>
		</Content>
		<Content
			:is-mobile="isXS"
			class="absolute bottom-0 pointer-events-none"
			:class="isXS ? 'pb-32' : 'pb-3'"
		>
			<div class="d-grid width-full align-end" :class="'bottom-container' + (isXS ? '-xs' : '')">
				<div>
					<ProfileDestinationsLegend v-if="!isXS" class="width-fit-content pointer-events-all" />
				</div>
				<Transition name="trip" mode="out-in">
					<ContinueUpdatingTrip
						v-if="lastUpdatedTrip && displayContinueEditing"
						:trip="lastUpdatedTrip"
						@close="displayContinueEditing = false"
						class="justify-self-center pointer-events-all"
						:class="isXS ? 'width-full' : 'width-fit-content'"
					/>
				</Transition>
				<div></div>
			</div>
		</Content>
	</div>
</template>

<script setup lang="ts">
	import { storeToRefs } from 'pinia'
	import { computed, onMounted, ref, Ref, shallowRef, useTemplateRef, watch } from 'vue'
	import { HomeMetric } from '~/actions/metrics/home'
	import { ProfileActions } from '~/actions/profile/action'
	import { ProfileDestinationsActions } from '~/actions/profile/destinations'
	import { ProfileTripActions } from '~/actions/profile/trip'
	import { DestinationActions } from '~/actions/site/destinationActions'
	import { Destination2 } from '~/models/Destination'
	import { ProfileDestination, ProfileDestinationType } from '~/models/ProfileDestination'
	import { TripNavigation } from '~/services/navigation/trip'
	import EventsService from '~/services/events'
	import { useDeviceStore } from '~/store/device'
	import { useProfileStore } from '~/store/profile'

	const profileStore = useProfileStore()
	const { profile } = storeToRefs(profileStore)

	const deviceStore = useDeviceStore()
	const { isXS } = storeToRefs(deviceStore)
	const route = useRoute()

	const wishlist = shallowRef<ProfileDestination[]>([])
	const visited = shallowRef<ProfileDestination[]>([])
	const isLoadingProfile = ref<boolean>(true)
	const isLoadingDestination = ref<boolean>(true)
	const destinationRequestId = ref<number>(0)

	const selectedDestination = ref<Destination2>()
	const globe = useTemplateRef('globe')

	const profileCountry = ref<Destination2>()
	const displayContinueEditing = ref<boolean>(true)
	const showDestinationDialog = ref<boolean>(false)
	const lastUpdatedTrip = await ProfileTripActions.getLastUpdatedTrip(profile.value!.id!)

	onMounted(async () => {
		await getDestinations()
		profileCountry.value = await ProfileActions.getCountry()
		isLoadingProfile.value = false
		const routeName = route.name?.toString()
		HomeMetric.viewHome(routeName)
		EventsService.listenProfileWizardCompleted(getDestinations)
	})

	watch(
		() => profile.value,
		async () => {
			profileCountry.value = await ProfileActions.getCountry()
		}
	)

	const getDestinations = async () => {
		const destinations = await ProfileDestinationsActions.getDestinations(profile.value!.id!)
		wishlist.value = destinations.filter((destination) => destination.type === 'wishlist')
		visited.value = destinations.filter((destination) => destination.type === 'visited')
	}

	const toggleVisited = async () => {
		await handleDestinationToggle('visited')
	}

	const toggleWishlist = async () => {
		await handleDestinationToggle('wishlist')
	}

	const openTripDialog = (destinationId: string) => {
		TripNavigation.toCreateTrip(destinationId)
	}

	const openDestinationDialog = async (iso: string) => {
		const requestId = ++destinationRequestId.value
		if (isXS.value) displayContinueEditing.value = false
		showDestinationDialog.value = true
		selectedDestination.value = undefined
		isLoadingDestination.value = true
		const destination = await DestinationActions.getByIsoCode(iso)
		if (requestId !== destinationRequestId.value) return
		selectedDestination.value = destination
		isLoadingDestination.value = false
	}

	const isWished = computed(() =>
		wishlist.value.some((item) => item.destinationId === selectedDestination.value?.id)
	)

	const isVisited = computed(() =>
		visited.value.some((item) => item.destinationId === selectedDestination.value?.id)
	)

	const closeDestination = () => {
		showDestinationDialog.value = false
		selectedDestination.value = undefined
		globe.value?.deselectCountry()
		selectedDestination.value = undefined
	}

	const handleDestinationToggle = async (type: ProfileDestinationType) => {
		if (!selectedDestination.value) return
		const list = getList(type)
		const found = list.value.find(
			(item) => item.destinationId === selectedDestination.value!.id && item.type === type
		)
		if (found) await removeDestination(found, type)
		else await addDestination(selectedDestination.value!, type)
		list.value = await ProfileDestinationsActions.resetDestinations(profile.value!.id!, type)
	}

	const removeDestination = async (
		profileDestination: ProfileDestination,
		type: ProfileDestinationType
	) => {
		const list = getList(type)
		await ProfileDestinationsActions.removeDestination(profile.value!.id!, profileDestination)
		list.value = list.value.filter((item) => item.id !== profileDestination.id)
	}

	const addDestination = async (destination: Destination2, type: ProfileDestinationType) => {
		const list = getList(type)
		const created = await ProfileDestinationsActions.addDestinations(
			profile.value!.id!,
			[destination],
			type
		)
		if (created?.[0]) list.value = [...list.value, created[0]]
	}

	const getList = (type: ProfileDestinationType): Ref<ProfileDestination[]> => {
		return type === 'wishlist' ? wishlist : visited
	}
</script>

<style lang="scss" scoped>
	.home-container {
		background-color: #77c1d8;
		height: 100vh;
		overflow: hidden;
	}
	.trip-enter-active,
	.trip-leave-active {
		transition:
			opacity 450ms ease,
			transform 450ms ease;
	}
	.trip-enter-from,
	.trip-leave-to {
		opacity: 0;
		transform: translateY(10px);
	}

	.globe-placeholder {
		background: radial-gradient(
			circle at 50% 50%,
			rgba(255, 255, 255, 0.45),
			rgba(119, 193, 216, 0.1)
		);
	}
	.bottom-container {
		grid-template-columns: 1fr auto 1fr;
		&-xs {
			grid-template-columns: 0 1fr 0;
		}
	}
	.map-button {
		backdrop-filter: blur(40px);
		background: #f6f6f64d;
		border-color: #ffffff33;
	}
	.globe-fade-enter-active {
		transition: opacity 3s;
	}
	.globe-fade-enter-from {
		opacity: 0;
	}
</style>
